// src/components/leaves/LeaveTypeForm.js
import { useState, useEffect } from 'react';

const LeaveTypeForm = ({ leaveType, onSubmit, onCancel, userType, userPremisesId, premises = [], submitting }) => {
  const [formData, setFormData] = useState({
    leave_name: '',
    fiscal_year: '',
    max_allowed_per_year: '',
    description: '',
    premises_id: userType === 'core_admin' ? '0' : '',
    apply_to_all_premises: false,
    status: '1'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (leaveType) {
      setFormData({
        leave_name: leaveType.leave_name || '',
        fiscal_year: leaveType.fiscal_year || '',
        max_allowed_per_year: leaveType.max_allowed_per_year || '',
        description: leaveType.description || '',
        premises_id: leaveType.premises_id || (userType === 'core_admin' ? '0' : ''),
        apply_to_all_premises: false,
        status: leaveType.status !== undefined ? String(leaveType.status) : '1'
      });
    }
  }, [leaveType, userType]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.leave_name.trim()) {
      newErrors.leave_name = 'Leave name is required';
    }

    if (!formData.max_allowed_per_year || formData.max_allowed_per_year < 0) {
      newErrors.max_allowed_per_year = 'Max allowed per year must be a positive number';
    }

    // Validate premises_id only if not applying to all premises
    if (!formData.apply_to_all_premises) {
      if ((userType === 'core_admin' || userType === 'client_admin' || userType === 'admin') && !formData.premises_id && formData.premises_id !== '0') {
        newErrors.premises_id = 'Please select premises/scope or check "Apply to All Premises"';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      if (error.errors) {
        setErrors(error.errors);
      } else {
        alert(error.message || 'Failed to save leave type');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="leave-type-form">
      <div className="form-content">
        {/* Leave Name */}
        <div className="form-group">
          <label htmlFor="leave_name">
            <i className="fas fa-tag"></i>
            Leave Type Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="leave_name"
            name="leave_name"
            className={`form-input ${errors.leave_name ? 'error' : ''}`}
            value={formData.leave_name}
            onChange={handleChange}
            placeholder="e.g., Casual Leave, Sick Leave"
            disabled={submitting}
          />
          {errors.leave_name && <span className="error-message">{errors.leave_name}</span>}
        </div>

        {/* Fiscal Year */}
        <div className="form-group">
          <label htmlFor="fiscal_year">
            <i className="fas fa-calendar"></i>
            Fiscal Year
          </label>
          <input
            type="text"
            id="fiscal_year"
            name="fiscal_year"
            className="form-input"
            value={formData.fiscal_year}
            onChange={handleChange}
            placeholder="e.g., 2024-2025"
            disabled={submitting}
          />
          <small className="help-text">Optional: Specify fiscal year for this leave type</small>
        </div>

        {/* Max Allowed Per Year */}
        <div className="form-group">
          <label htmlFor="max_allowed_per_year">
            <i className="fas fa-calculator"></i>
            Maximum Allowed Per Year <span className="required">*</span>
          </label>
          <input
            type="number"
            id="max_allowed_per_year"
            name="max_allowed_per_year"
            className={`form-input ${errors.max_allowed_per_year ? 'error' : ''}`}
            value={formData.max_allowed_per_year}
            onChange={handleChange}
            placeholder="Enter maximum days (0 for unlimited)"
            min="0"
            disabled={submitting}
          />
          {errors.max_allowed_per_year && <span className="error-message">{errors.max_allowed_per_year}</span>}
          <small className="help-text">Enter 0 for unlimited leave days</small>
        </div>

        {/* Apply to All Premises Checkbox - For non-core-admin */}
        {(userType === 'client_admin' || userType === 'admin') && !leaveType && (
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="apply_to_all_premises"
                checked={formData.apply_to_all_premises}
                onChange={handleChange}
                disabled={submitting}
                className="checkbox-input"
              />
              <span className="checkbox-text">
                <i className="fas fa-building"></i>
                Apply to All Premises in Organization
              </span>
            </label>
            <small className="help-text">
              {formData.apply_to_all_premises
                ? 'This leave type will be created for all premises in your organization'
                : 'Uncheck to select a specific premises'}
            </small>
          </div>
        )}

        {/* Scope - For core_admin and client_admin */}
        {(userType === 'core_admin' || userType === 'client_admin' || userType === 'admin') && !formData.apply_to_all_premises && (
          <div className="form-group">
            <label htmlFor="premises_id">
              <i className="fas fa-building"></i>
              {userType === 'core_admin' ? 'Leave Type Scope' : 'Select Premises'} <span className="required">*</span>
            </label>
            <select
              id="premises_id"
              name="premises_id"
              className={`form-input ${errors.premises_id ? 'error' : ''}`}
              value={formData.premises_id}
              onChange={handleChange}
              disabled={submitting || (leaveType && leaveType.id)}
            >
              <option value="">Select {userType === 'core_admin' ? 'Scope' : 'Premises'}</option>
              {userType === 'core_admin' && (
                <option value="0">Common (All Organizations)</option>
              )}
              {premises && premises.length > 0 && premises.map(premise => (
                <option key={premise.id} value={premise.id}>
                  {premise.premises_name}
                </option>
              ))}
            </select>
            {errors.premises_id && <span className="error-message">{errors.premises_id}</span>}
            <small className="help-text">
              {userType === 'core_admin'
                ? 'Common leave types are available to all organizations.'
                : 'This leave type will only be available for the selected premises.'
              }
              {leaveType && leaveType.id && ' (Cannot change after creation)'}
            </small>
          </div>
        )}

        {/* Description */}
        <div className="form-group">
          <label htmlFor="description">
            <i className="fas fa-align-left"></i>
            Description
          </label>
          <textarea
            id="description"
            name="description"
            className="form-input"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief description of this leave type..."
            rows="4"
            disabled={submitting}
          ></textarea>
        </div>

        {/* Status */}
        <div className="form-group">
          <label htmlFor="status">
            <i className="fas fa-toggle-on"></i>
            Status
          </label>
          <select
            id="status"
            name="status"
            className="form-input"
            value={formData.status}
            onChange={handleChange}
            disabled={submitting}
          >
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <small className="help-text">Inactive leave types cannot be selected for new requests</small>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <i className="fas fa-info-circle"></i>
          <div>
            <strong>Note:</strong>
            {userType === 'core_admin' ? (
              <p>As a core admin, you can create common leave types (available to all organizations) or organization-specific types (applied to all premises in that organization).</p>
            ) : formData.apply_to_all_premises ? (
              <p>This leave type will be created for <strong>all premises</strong> in your organization. All staff across all premises will be able to use this leave type.</p>
            ) : (
              <p>This leave type will be specific to the selected premises only. Staff in that premises will be able to use this leave type.</p>
            )}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          <i className="fas fa-times"></i>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save"></i>
              {leaveType ? 'Update' : 'Create'} Leave Type
            </>
          )}
        </button>
      </div>

      <style jsx>{`
        .leave-type-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #2d3748;
          font-size: 0.95rem;
        }

        .form-group label i {
          color: #f59e0b;
          font-size: 0.9rem;
        }

        .required {
          color: #ef4444;
          font-weight: 700;
        }

        .form-input {
          padding: 0.75rem 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
          color: #2d3748;
        }

        .form-input:focus {
          outline: none;
          border-color: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
        }

        .form-input.error {
          border-color: #ef4444;
        }

        .form-input:disabled {
          background: #f1f5f9;
          cursor: not-allowed;
          opacity: 0.7;
        }

        textarea.form-input {
          resize: vertical;
          min-height: 100px;
          font-family: inherit;
        }

        .help-text {
          color: #718096;
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }

        .error-message {
          color: #ef4444;
          font-size: 0.85rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .error-message::before {
          content: '⚠';
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          padding: 1rem;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 2px solid #bae6fd;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .checkbox-label:hover {
          border-color: #7dd3fc;
          box-shadow: 0 2px 8px rgba(14, 165, 233, 0.1);
        }

        .checkbox-input {
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #f59e0b;
        }

        .checkbox-text {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #0c4a6e;
          font-size: 0.95rem;
        }

        .checkbox-text i {
          color: #0284c7;
        }

        .info-box {
          display: flex;
          gap: 1rem;
          padding: 1rem 1.2rem;
          background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
          border: 1px solid #bfdbfe;
          border-radius: 10px;
          color: #1e40af;
        }

        .info-box i {
          color: #3b82f6;
          font-size: 1.2rem;
          flex-shrink: 0;
          margin-top: 0.2rem;
        }

        .info-box strong {
          font-weight: 600;
          display: block;
          margin-bottom: 0.3rem;
        }

        .info-box p {
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 2px solid #e2e8f0;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }

        .btn-primary {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          box-shadow: 0 5px 15px rgba(245, 158, 11, 0.3);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(245, 158, 11, 0.4);
        }

        .btn-secondary {
          background: white;
          color: #718096;
          border: 2px solid #e2e8f0;
        }

        .btn-secondary:hover:not(:disabled) {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .fa-spin {
          animation: fa-spin 1s infinite linear;
        }

        @keyframes fa-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .form-actions {
            flex-direction: column-reverse;
          }

          .btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </form>
  );
};

export default LeaveTypeForm;
